import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, useTheme } from '@mui/material';
import { documentApi, documentsApi } from 'app/services/api';
import ScrollWrapper from 'app/shared/components/common/ScrollWrapper';
import DocumentsTreeView from 'app/shared/components/document/DocumentsTreeView';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { SystemCategoryIdList } from 'constants/documentCategories';
import { DOCUMENT_REJECTED_PLACEHOLDER } from 'constants/documents';
import usePrevious from 'hooks/usePrevious';
import { checkIsSafari } from 'utils/browser';
import { getConditionalObject } from 'utils/data';
import { getRecipientCopyMasterIds } from 'utils/documents';
import { logger } from 'utils/logger';
import { getPdfJsDocument, mergePdfs } from 'utils/pdf';
import { compareBySearchValue } from 'utils/searchItems';

import {
  deleteDocumentAction,
  getDocumentsAction,
  moveDocumentToTypeAction,
  resetSelectedDocumentsAction,
} from 'store/documents/actions';
import { getDocuments, getSelectedDocumentsIds } from 'store/documents/selectors';
import { openDialogAction, showErrorMessageAction, showMessageAction } from 'store/global/actions';
import { getDebouncedSearchValue } from 'store/search/selectors';
import { getCategoriesGroupedByTypeId, selectPreviewDocuments } from 'store/system/selectors';

import CopyDocumentsForm from './CopyDocumentsForm';
import { SCROLL_DELAY_MS, SIGNED_DOCUMENTS_MERGE_MESSAGE } from './DocumentsTree.constants';
import { getStyles } from './DocumentsTree.styles';
import {
  checkCanMergeDocuments,
  checkIsDocumentMergeable,
  checkIsDocumentToggledByClick,
  getInitialExpandedItemIds,
} from './DocumentsTree.utils';
import DocumentTypeItem from './DocumentTypeItem';
import Header from './Header';
import SelectionActionBar from './SelectionActionBar';

const initialMergeState = {
  documentItemsToMerge: [],
  activeDocumentId: '',
  documentTypeId: '',
  possibleDocumentItemsToMerge: [],
};

const DocumentTreeView = forwardRef(
  (
    {
      documentId = '',
      isView = true,
      isDataLoaded = false,
      loanParams,
      isLoading,
      setIsLoading,
      onCustomNavigateDocument,
      onOpenDocumentItemContextMenu,
      isCopyDocumentsAllowed = false,
      onOpenAiBatchIntake,
      onCollapsePanelLeft = null,
    },
    ref,
  ) => {
    const { applicationId } = useParams();

    const isSafari = checkIsSafari();

    const theme = useTheme();

    const classes = getStyles(theme, { isView });

    const scrollWrapperClassName = isSafari ? classes.documentsListWrapper : undefined;

    const dispatch = useDispatch();

    const treeRef = useRef();
    const mouseOveredCategoryId = useRef(null);
    const mouseOverHandlerTimeout = useRef(null);

    const [expandedList, setExpandedList] = useState([]);
    const [draggableData, setDraggableData] = useState(null);
    const [searchList, setSearchList] = useState([]);
    const [filteredTree, setFilteredTree] = useState([]);
    const [mergeState, setMergeState] = useState(initialMergeState);
    const [isEmptyTreeItemVisible, changeEmptyTreeItemVisibility] = useState(false);
    const [isDeletedDocumentsVisible, setIsDeletedDocumentsVisible] = useState(false);
    const [isRejectedOnlyVisible, setIsRejectedOnlyVisible] = useState(false);
    const [isAllExpanded, setIsAllExpanded] = useState(true);

    const documents = useSelector(getDocuments);
    const selectedDocumentsIds = useSelector(getSelectedDocumentsIds);
    const previewDocuments = useSelector(selectPreviewDocuments(isDeletedDocumentsVisible));
    const groupedCategories = useSelector(getCategoriesGroupedByTypeId);
    const searchText = useSelector(getDebouncedSearchValue);

    const isActiveDocumentDeleted = !!documents[documentId]?.isDeleted;

    const previousIsDataLoaded = usePrevious(isDataLoaded);

    const isSkeleton = isLoading && !!filteredTree?.length;
    const documentTypeId = documents[documentId]?.documentTypeId;
    const activeRecipientCopyMasterId = documents[documentId]?.RecipientCopyOf ?? null;

    const onClearSelection = useCallback(() => {
      dispatch(resetSelectedDocumentsAction());
    }, [dispatch]);

    const onCopyDocuments = useCallback(() => {
      dispatch(
        openDialogAction({
          isRestyled: true,
          header: 'Copy Documents to Another Loan',
          withButtons: true,
          onAcceptButtonName: 'Copy',
          body: ({ dialogId }) => (
            <CopyDocumentsForm
              applicationId={applicationId}
              dialogId={dialogId}
              loanId={loanParams.loanIdOrLegacyLoanNumber}
            />
          ),
        }),
      );
    }, [dispatch, loanParams.loanIdOrLegacyLoanNumber, applicationId]);

    const headerToolbarOptions = useMemo(
      () => ({
        ...getConditionalObject(isCopyDocumentsAllowed, {
          onCopyDocuments,
          isCopyDocumentsDisabled: !selectedDocumentsIds?.length,
        }),
        ...getConditionalObject(!!onOpenAiBatchIntake, {
          onOpenAiBatchIntake,
        }),
      }),
      [isCopyDocumentsAllowed, onCopyDocuments, onOpenAiBatchIntake, selectedDocumentsIds?.length],
    );

    const filterItem = useCallback(
      (accum, item) => {
        const isNodeIncludesSearchValue = [item.documentCategoryName, item.name].some(prop =>
          compareBySearchValue(prop, searchText),
        );

        if (isNodeIncludesSearchValue) {
          accum.push(item);

          return accum;
        }

        if (item.types) {
          const filteredTypes = item.types.reduce(filterItem, []);

          if (filteredTypes?.length) {
            setSearchList(currentList => [...currentList, item.documentCategoryId]);
            accum.push({ ...item, types: filteredTypes });
          }
        }

        return accum;
      },
      [searchText],
    );

    useEffect(() => {
      const filteredItems = searchText
        ? previewDocuments?.reduce(filterItem, [])
        : previewDocuments;

      setSearchList([]);
      setFilteredTree(filteredItems || []);
    }, [searchText, previewDocuments, filterItem]);

    useEffect(() => {
      if (!isDataLoaded) {
        return;
      }

      if (documents[documentId]) {
        const possibleDocumentItemsToMerge = Object.values(documents).reduce((arr, document) => {
          if (document.documentTypeId === documentTypeId && checkIsDocumentMergeable(document)) {
            arr.push(document.id);
          }

          return arr;
        }, []);

        setMergeState({
          documentItemsToMerge: [],
          activeDocumentId: documentId,
          documentTypeId,
          possibleDocumentItemsToMerge,
        });
      }

      if (!documents[documentId]) {
        setMergeState(initialMergeState);
      }
    }, [documentId, documentTypeId, documents, isDataLoaded]);

    const totalDocumentCount = useMemo(
      () =>
        filteredTree.reduce((total, { types }) => {
          const typeDocCount = types?.reduce((acc, type) => acc + type.types.length, 0) ?? 0;

          return total + typeDocCount;
        }, 0),
      [filteredTree],
    );

    const displayedTree = useMemo(() => {
      if (!isRejectedOnlyVisible) {
        return filteredTree;
      }

      return filteredTree.filter(
        ({ documentCategoryId }) => documentCategoryId === DOCUMENT_REJECTED_PLACEHOLDER,
      );
    }, [filteredTree, isRejectedOnlyVisible]);

    const activeCategories = useMemo(() => {
      const categoriesTree = isEmptyTreeItemVisible
        ? filteredTree
        : filteredTree.filter(({ documentCategoryId: id, types }) => {
            if (SystemCategoryIdList.includes(id)) {
              return false;
            }

            return !!types?.reduce((acc, type) => acc + type.types.length, 0);
          });

      return categoriesTree.map(({ documentCategoryId }) => documentCategoryId);
    }, [filteredTree, isEmptyTreeItemVisible]);

    const setExpandedListCallback = useCallback(() => {
      const categoriesIds = filteredTree.map(({ documentCategoryId }) => documentCategoryId);

      setExpandedList(
        getInitialExpandedItemIds({
          categoriesIds,
          activeDocument: documents?.[documentId] ?? null,
          groupedCategories,
        }),
      );
    }, [documentId, documents, filteredTree, groupedCategories]);

    useEffect(() => {
      if (!isDataLoaded || previousIsDataLoaded) {
        return;
      }

      setExpandedListCallback();
    }, [
      isDataLoaded,
      documentId,
      previousIsDataLoaded,
      documents,
      groupedCategories,
      filteredTree,
      isEmptyTreeItemVisible,
      setExpandedListCallback,
    ]);

    // The open document is outside the tree's control and the group must stay closable, so
    // the master is only added when the open copy changes.
    useEffect(() => {
      if (!activeRecipientCopyMasterId) {
        return;
      }

      setExpandedList(prevList =>
        prevList.includes(activeRecipientCopyMasterId)
          ? prevList
          : [...prevList, activeRecipientCopyMasterId],
      );
    }, [documentId, activeRecipientCopyMasterId]);

    useEffect(() => {
      const changedCategories = _.difference(activeCategories, expandedList);

      setIsAllExpanded(!changedCategories.length);
    }, [activeCategories, expandedList]);

    const onDocumentItemClickWithCmd = useCallback(
      ({ id, isDeleted: isTargetElementDeleted }) => {
        if (isActiveDocumentDeleted || isTargetElementDeleted === 'true') {
          return;
        }

        if (
          mergeState.possibleDocumentItemsToMerge.includes(id) &&
          mergeState.activeDocumentId !== id
        ) {
          setMergeState({
            ...mergeState,
            documentItemsToMerge: mergeState.documentItemsToMerge.includes(id)
              ? mergeState.documentItemsToMerge.filter(i => i !== id)
              : [...mergeState.documentItemsToMerge, id],
          });
        }
      },
      [isActiveDocumentDeleted, mergeState],
    );

    const onDocumentItemClickWithShift = useCallback(
      ({ id, isDeleted: isTargetElementDeleted }) => {
        if (isActiveDocumentDeleted || isTargetElementDeleted === 'true') {
          return;
        }

        const indexOfActiveDocumentId = mergeState.possibleDocumentItemsToMerge.indexOf(
          mergeState.activeDocumentId,
        );
        const indexOfSelectedDocumentId = mergeState.possibleDocumentItemsToMerge.indexOf(id);

        const from = Math.min(indexOfActiveDocumentId, indexOfSelectedDocumentId);
        const to = Math.max(indexOfActiveDocumentId, indexOfSelectedDocumentId);

        setMergeState({
          ...mergeState,
          documentItemsToMerge: mergeState.possibleDocumentItemsToMerge
            .slice(from, to + 1)
            .filter(id => id !== mergeState.activeDocumentId),
        });
      },
      [isActiveDocumentDeleted, mergeState],
    );

    const onDocumentItemClick = useCallback(
      e => {
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          onDocumentItemClickWithCmd(e.currentTarget.dataset);

          return;
        }

        if (e.shiftKey) {
          e.preventDefault();
          onDocumentItemClickWithShift(e.currentTarget.dataset);

          return;
        }

        if (onCustomNavigateDocument) {
          e.preventDefault();
          onCustomNavigateDocument(e.currentTarget.dataset.id);
        }
      },
      [onDocumentItemClickWithCmd, onDocumentItemClickWithShift, onCustomNavigateDocument],
    );

    const onMergePdf = useCallback(async () => {
      if (!loanParams.loanIdOrLegacyLoanNumber && !loanParams.subCompanyId) {
        logger.error('Document owner id (loan or subcompany) unavailable, but required');
      }

      const sortedDocumentIds = [...mergeState.documentItemsToMerge, mergeState.activeDocumentId];

      if (!checkCanMergeDocuments(documents, sortedDocumentIds)) {
        dispatch(
          showMessageAction({ message: SIGNED_DOCUMENTS_MERGE_MESSAGE, variant: 'warning' }),
        );

        return;
      }

      setIsLoading(true);
      sortedDocumentIds.sort(
        // merge order is from top to bottom
        (a, b) =>
          mergeState.possibleDocumentItemsToMerge.indexOf(a) -
          mergeState.possibleDocumentItemsToMerge.indexOf(b),
      );

      try {
        // One document at a time: only one blob and array buffer is live at a time rather
        // than one per selected document, and the documents service sees one download at a
        // time instead of N at once. The parsed documents are all retained — mergePdfs
        // needs them.
        const pdfsToMerge = await sortedDocumentIds.reduce(async (pdfsPromise, documentId) => {
          const pdfs = await pdfsPromise;
          const { data } = await documentsApi.getContent({ documentId });
          const loadingTask = await getPdfJsDocument({ data: await data.arrayBuffer() });
          const pdf = await loadingTask.promise;

          return [...pdfs, pdf];
        }, Promise.resolve([]));

        const mergedPdf = await mergePdfs(pdfsToMerge);

        const fd = new FormData();
        fd.append('file', new Blob([mergedPdf], { type: 'application/pdf' }));

        const params = {
          ...loanParams,
          formData: fd,
          documentTypeId: mergeState.documentTypeId,
        };

        const {
          data: { id: documentId },
        } = await documentApi.createDocumentV2(params);

        await dispatch(getDocumentsAction(loanParams));
        dispatch(showMessageAction({ message: 'Successfully merged pdf', variant: 'success' }));

        sortedDocumentIds.forEach(documentId => dispatch(deleteDocumentAction({ documentId })));

        onCustomNavigateDocument(documentId);
      } catch (err) {
        dispatch(showErrorMessageAction({ message: "Couldn't merge pdf", err }));
        logger.error(err);
      } finally {
        setIsLoading(false);
      }
    }, [dispatch, documents, loanParams, mergeState, setIsLoading, onCustomNavigateDocument]);

    const onUpdateDocument = useCallback(
      ({ typeId, documentId }) => {
        setIsLoading(true);

        dispatch(moveDocumentToTypeAction({ documentId, documentTypeId: typeId, loanParams }))
          .then(() => setExpandedList(prevList => _.uniq([...prevList, typeId])))
          .catch(Error)
          .finally(() => setIsLoading(false));
      },
      [dispatch, loanParams, setIsLoading],
    );

    const onDragStart = useCallback(
      e => {
        const { id: documentId } = e.currentTarget;
        const typeId = e.currentTarget.getAttribute('data-type-id');
        setDraggableData({ documentId, typeId });
      },
      [setDraggableData],
    );

    const onDragEnd = useCallback(
      e => {
        e.preventDefault();
        e.stopPropagation();

        const { id: typeId } = e.currentTarget;
        const isRejectedType = typeId.startsWith(DOCUMENT_REJECTED_PLACEHOLDER);
        const isDraggable = draggableData && draggableData.typeId !== typeId && !isRejectedType;

        if (isDraggable) {
          const { documentId } = draggableData;

          onUpdateDocument({ typeId, documentId });

          setDraggableData(null);
        }
      },
      [onUpdateDocument, draggableData],
    );

    const onDragOver = useCallback(
      e => {
        e.preventDefault();
        const { id } = e.currentTarget;

        if (mouseOveredCategoryId.current === id) {
          return;
        }

        clearTimeout(mouseOverHandlerTimeout.current);
        mouseOveredCategoryId.current = id;
        mouseOverHandlerTimeout.current = setTimeout(() => {
          treeRef.current?.scrollIntoView(false);

          if (draggableData && mouseOveredCategoryId.current === id) {
            setExpandedList(prevList => _.uniq([...prevList, id]));
          }
        }, SCROLL_DELAY_MS);
      },
      [draggableData],
    );

    const handleCollapse = useCallback(() => {
      const categoriesWithDocTypes = _.uniq([
        ...activeCategories,
        ...expandedList,
        ...getRecipientCopyMasterIds(documents),
      ]);

      setIsAllExpanded(prev => !prev);
      setExpandedList(isAllExpanded ? [] : categoriesWithDocTypes);
    }, [activeCategories, documents, expandedList, isAllExpanded]);

    const onDragLeave = useCallback(() => {
      clearTimeout(mouseOverHandlerTimeout.current);
      mouseOveredCategoryId.current = null;
    }, []);

    const onToggleRecipientCopies = useCallback(masterDocumentId => {
      setExpandedList(prevList => _.xor(prevList, [masterDocumentId]));
    }, []);

    const onChangeEmptyTreeItemVisibility = useCallback(() => {
      changeEmptyTreeItemVisibility(prevValue => !prevValue);
      setExpandedList(isEmptyTreeItemVisible ? activeCategories : []);
      setIsAllExpanded(isEmptyTreeItemVisible);
    }, [activeCategories, isEmptyTreeItemVisible]);

    const onChangeDeletedDocumentsVisibility = useCallback(() => {
      setIsDeletedDocumentsVisible(prevValue => !prevValue);
    }, []);

    const onChangeRejectedOnlyVisibility = useCallback(() => {
      setIsRejectedOnlyVisible(prevValue => !prevValue);
    }, []);

    const expanded = useMemo(() => {
      return [...expandedList, ...searchList];
    }, [expandedList, searchList]);

    const onNodeToggle = useCallback(
      (event, nodeIds) => {
        if (
          checkIsDocumentToggledByClick({
            event,
            prevExpandedItems: expanded,
            nextExpandedItems: nodeIds,
            documents,
          })
        ) {
          return;
        }

        setExpandedList(nodeIds);
      },
      [documents, expanded],
    );

    useImperativeHandle(
      ref,
      () => ({
        onUpdateDocument,
      }),
      [onUpdateDocument],
    );

    useEffect(() => {
      return () => {
        dispatch(resetSelectedDocumentsAction());
      };
    }, [dispatch]);

    const renderTypes = useCallback(
      types => (
        <DocumentTypeItem
          documentId={documentId}
          documentItemsToMerge={mergeState.documentItemsToMerge}
          expandedList={expanded}
          isActiveDocumentDeleted={isActiveDocumentDeleted}
          isCopyDocumentsAllowed={isCopyDocumentsAllowed}
          isDragging={!!draggableData}
          isEmptyTreeItemVisible={isEmptyTreeItemVisible}
          items={types}
          loanParams={loanParams}
          setDraggableData={setDraggableData}
          setExpandedList={setExpandedList}
          onCustomNavigateDocument={onCustomNavigateDocument}
          onDocumentItemClick={onDocumentItemClick}
          onDragEnd={onDragEnd}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDragStart={onDragStart}
          onOpenDocumentItemContextMenu={onOpenDocumentItemContextMenu}
          onToggleRecipientCopies={onToggleRecipientCopies}
        />
      ),
      [
        expanded,
        documentId,
        draggableData,
        isEmptyTreeItemVisible,
        isActiveDocumentDeleted,
        loanParams,
        mergeState.documentItemsToMerge,
        onDocumentItemClick,
        onDragEnd,
        onDragLeave,
        onDragOver,
        onDragStart,
        onCustomNavigateDocument,
        onOpenDocumentItemContextMenu,
        onToggleRecipientCopies,
        isCopyDocumentsAllowed,
      ],
    );

    return (
      <Box ref={ref} sx={classes.documentTree}>
        <Header
          isAllExpanded={isAllExpanded}
          isDeletedDocumentsVisible={isDeletedDocumentsVisible}
          isEmptyTreeItemVisible={isEmptyTreeItemVisible}
          isRejectedOnlyVisible={isRejectedOnlyVisible}
          loanParams={loanParams}
          toolbarOptions={headerToolbarOptions}
          totalDocumentCount={totalDocumentCount}
          onChangeDeletedDocumentsVisibility={onChangeDeletedDocumentsVisibility}
          onChangeEmptyTreeItemVisibility={onChangeEmptyTreeItemVisibility}
          onChangeRejectedOnlyVisibility={onChangeRejectedOnlyVisibility}
          onCollapse={handleCollapse}
          onCollapsePanelLeft={onCollapsePanelLeft}
        />

        <ScrollWrapper
          className={scrollWrapperClassName}
          isDragging={!!draggableData}
          scrollRef={treeRef}
        >
          {!!selectedDocumentsIds?.length && (
            <SelectionActionBar
              isCopyDocumentsAllowed={isCopyDocumentsAllowed}
              isLoading={isLoading}
              selectedCount={selectedDocumentsIds.length}
              onClearSelection={onClearSelection}
              onCopyDocuments={headerToolbarOptions.onCopyDocuments}
              onMergePdf={onMergePdf}
            />
          )}
          <DocumentsTreeView
            expanded={expanded}
            isEmptyTreeItemVisible={isEmptyTreeItemVisible}
            isSkeleton={isSkeleton}
            isView={isView}
            renderTypesCallback={renderTypes}
            tree={displayedTree}
            treeRef={treeRef}
            onNodeToggle={onNodeToggle}
          />
        </ScrollWrapper>
      </Box>
    );
  },
);

DocumentTreeView.propTypes = {
  loanParams: PropTypes.object.isRequired,
  documentId: PropTypes.string,
  isView: PropTypes.bool,
  isDataLoaded: PropTypes.bool,
  isCopyDocumentsAllowed: PropTypes.bool,
  onCustomNavigateDocument: PropTypes.func,
  onOpenDocumentItemContextMenu: PropTypes.func,
  onOpenAiBatchIntake: PropTypes.func,
  onCollapsePanelLeft: PropTypes.func,
  ...WithLoadingPropTypes,
};

export default memo(DocumentTreeView);
